// src/pages/PaymentsPage.js
import React, { useMemo, useState } from 'react';
import Avatar from '../schedule/Avatar';

const POSITIONS = [
    { key: 'reżyser', label: 'Reżyser' },
    { key: 'showcaller', label: 'Showcaller' },
    { key: 'stage_manager', label: 'Stage Manager' },
    { key: 'inspicjent', label: 'Inspicjent' },
];

const MONTHS = [
    'Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec',
    'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień',
];

/**
 * Zakładka $$$ - wyświetla wydarzenia z kwotami dla każdego pracownika
 */
export default function PaymentsPage({
    employees = [],
    eventsByDate = {},
    paymentRates = {}, // { [employeeName]: { [position]: amount } }
    eventStaffRoles = {}, // { [date]: { [eventName]: { [employeeName]: position } } }
    eventAmounts = {}, // { [date]: { [eventName]: amount } }
    eventStaffPayments = {}, // { [date]: { [eventName]: { [employeeName]: { baseAmount, additionalCosts } } } }
    defaultFunctionRates = {}, // { [position]: amount } - domyślne kwoty za funkcje (oferowane klientom)
    taxRate = 0, // procent podatku
    onUpdateRole, // (date, eventName, employeeName, position) => void
    onUpdateAmount, // (date, eventName, amount) => void
    onUpdateStaffPayment, // (date, eventName, employeeName, baseAmount, additionalCosts) => void
    selectedCalendar, // tylko wydarzenia z main calendar
    isAdmin = false,
}) {
    // Aktualny miesiąc jako domyślny
    const today = new Date();
    const currentMonthKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
    const [activeMonthKey, setActiveMonthKey] = useState(currentMonthKey);

    // Filtruj wydarzenia tylko z main calendar i aktywnego miesiąca
    const monthEvents = useMemo(() => {
        const [year, month] = activeMonthKey.split('-');
        const events = [];
        
        Object.entries(eventsByDate || {}).forEach(([date, eventList]) => {
            // Sprawdź czy data należy do aktywnego miesiąca
            if (date.startsWith(`${year}-${month}`)) {
                eventList.forEach((event) => {
                    if (!event.event || event._isFreeDay) return;
                    
                    // Tylko wydarzenia z main calendar
                    if (selectedCalendar && event.calendarId && event.calendarId !== selectedCalendar) {
                        return;
                    }
                    
                    const eventAmount = eventAmounts[date]?.[event.event] || 0;
                    const eventRoles = eventStaffRoles[date] || {};
                    const rolesForEvent = eventRoles[event.event] || {};
                    
                    // Zbierz pracowników z ich stanowiskami i kwotami
                    const staffPayments = eventStaffPayments[date]?.[event.event] || {};
                    const staffWithDetails = (event.staff || []).map((s) => {
                        const sName = typeof s === 'string' ? s : s?.name;
                        const position = rolesForEvent[sName] || null;
                        
                        // Pobierz zapisane kwoty dla tego pracownika
                        const empPayment = staffPayments[sName] || {};
                        
                        // Jeśli nie ma zapisanej kwoty, użyj domyślnej z paymentRates dla stanowiska
                        const empRates = paymentRates[sName] || {};
                        const defaultRateForPosition = position ? (empRates[position] || 0) : 0;
                        
                        const baseAmount = empPayment.baseAmount !== undefined 
                            ? empPayment.baseAmount 
                            : defaultRateForPosition;
                        const additionalCosts = empPayment.additionalCosts || 0;
                        
                        return {
                            name: sName,
                            position,
                            baseAmount,
                            additionalCosts,
                            total: baseAmount + additionalCosts,
                        };
                    }).filter(s => s.name);
                    
                    // Oblicz sugerowaną kwotę za wydarzenie na podstawie funkcji
                    const suggestedAmount = staffWithDetails.reduce((sum, s) => {
                        if (s.position) {
                            const functionRate = defaultFunctionRates[s.position] || 0;
                            return sum + functionRate;
                        }
                        return sum;
                    }, 0);

                    events.push({
                        date,
                        eventName: event.event,
                        calendarId: event.calendarId,
                        amount: eventAmount || (suggestedAmount > 0 ? suggestedAmount : 0),
                        suggestedAmount, // sugerowana kwota na podstawie funkcji
                        staff: staffWithDetails,
                    });
                });
            }
        });
        
        // Sortuj po dacie
        events.sort((a, b) => a.date.localeCompare(b.date));
        
        return events;
    }, [eventsByDate, activeMonthKey, selectedCalendar, eventAmounts, eventStaffRoles, paymentRates, eventStaffPayments, defaultFunctionRates]);

    // Funkcje do aktualizacji kwot (definiowane tutaj, aby mieć dostęp do eventStaffPayments)

    // Oblicz podsumowania dla każdego pracownika w miesiącu
    const employeeMonthlyTotals = useMemo(() => {
        const totals = {};
        
        monthEvents.forEach((event) => {
            event.staff.forEach((staffMember) => {
                if (!totals[staffMember.name]) {
                    totals[staffMember.name] = 0;
                }
                totals[staffMember.name] += staffMember.total;
            });
        });
        
        return totals;
    }, [monthEvents]);

    // Funkcje pomocnicze (zdefiniowane po użyciu w monthEvents)
    const handleUpdateBaseAmountLocal = (date, eventName, employeeName, value) => {
        const numValue = parseFloat(value) || 0;
        const staffPayments = eventStaffPayments[date]?.[eventName] || {};
        const currentPayment = staffPayments[employeeName] || {};
        const additionalCosts = currentPayment.additionalCosts || 0;
        
        if (typeof onUpdateStaffPayment === 'function') {
            onUpdateStaffPayment(date, eventName, employeeName, numValue, additionalCosts);
        }
    };

    const handleUpdateAdditionalCostsLocal = (date, eventName, employeeName, value) => {
        const numValue = parseFloat(value) || 0;
        const staffPayments = eventStaffPayments[date]?.[eventName] || {};
        const currentPayment = staffPayments[employeeName] || {};
        const baseAmount = currentPayment.baseAmount !== undefined ? currentPayment.baseAmount : 0;
        
        if (typeof onUpdateStaffPayment === 'function') {
            onUpdateStaffPayment(date, eventName, employeeName, baseAmount, numValue);
        }
    };

    // Oblicz podsumowanie miesiąca
    const monthSummary = useMemo(() => {
        const totalEventAmounts = monthEvents.reduce((sum, ev) => sum + (ev.amount || 0), 0);
        const totalStaffPayments = Object.values(employeeMonthlyTotals).reduce((sum, total) => sum + total, 0);
        const taxAmount = totalEventAmounts * (taxRate / 100);
        
        return {
            totalEventAmounts,
            totalStaffPayments,
            taxAmount,
            taxRate,
            difference: totalEventAmounts - taxAmount - totalStaffPayments,
        };
    }, [monthEvents, employeeMonthlyTotals, taxRate]);

    // Generuj opcje miesiąców (ostatnie 12 + przyszłe)
    const availableMonths = useMemo(() => {
        const months = [];
        const current = new Date();
        current.setMonth(current.getMonth() - 6); // 6 miesięcy wstecz
        
        for (let i = 0; i < 18; i++) {
            const year = current.getFullYear();
            const month = String(current.getMonth() + 1).padStart(2, '0');
            const key = `${year}-${month}`;
            const monthName = MONTHS[current.getMonth()];
            
            months.push({
                key,
                label: `${monthName} ${year}`,
                value: `${monthName} ${year}`,
            });
            
            current.setMonth(current.getMonth() + 1);
        }
        
        return months;
    }, []);

    const handleUpdateBaseAmount = (date, eventName, employeeName, value) => {
        const numValue = parseFloat(value) || 0;
        const staffPayments = eventStaffPayments[date]?.[eventName] || {};
        const currentPayment = staffPayments[employeeName] || {};
        const additionalCosts = currentPayment.additionalCosts || 0;
        
        if (typeof onUpdateStaffPayment === 'function') {
            onUpdateStaffPayment(date, eventName, employeeName, numValue, additionalCosts);
        }
    };

    const handleUpdateAdditionalCosts = (date, eventName, employeeName, value) => {
        const numValue = parseFloat(value) || 0;
        const staffPayments = eventStaffPayments[date]?.[eventName] || {};
        const currentPayment = staffPayments[employeeName] || {};
        const baseAmount = currentPayment.baseAmount !== undefined ? currentPayment.baseAmount : 0;
        
        if (typeof onUpdateStaffPayment === 'function') {
            onUpdateStaffPayment(date, eventName, employeeName, baseAmount, numValue);
        }
    };

    return (
        <div className="p-2 sm:p-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
                <h1 className="text-xl sm:text-2xl font-bold">💰 Podsumowanie wynagrodzeń</h1>
                
                {/* Selektor miesiąca */}
                <select
                    value={activeMonthKey}
                    onChange={(e) => setActiveMonthKey(e.target.value)}
                    className="w-full sm:w-auto px-3 sm:px-4 py-2 rounded border text-sm sm:text-base
                               bg-white dark:bg-gray-700
                               border-gray-300 dark:border-gray-600
                               text-gray-900 dark:text-gray-100"
                >
                    {availableMonths.map((month) => (
                        <option key={month.key} value={month.key}>
                            {month.label}
                        </option>
                    ))}
                </select>
            </div>

            {/* Kafelki wydarzeń */}
            {monthEvents.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    Brak wydarzeń w wybranym miesiącu
                </div>
            ) : (
                <div className="space-y-6 mb-8">
                    {monthEvents.map((event, idx) => {
                        const eventTotal = event.staff.reduce((sum, s) => sum + s.total, 0);
                        
                        return (
                            <div
                                key={idx}
                                className="border rounded-lg p-3 sm:p-4
                                           bg-white dark:bg-gray-800
                                           border-gray-200 dark:border-gray-700"
                            >
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                                        <div>
                                            <h2 className="text-base sm:text-lg font-semibold break-words">{event.eventName}</h2>
                                            <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">{event.date}</p>
                                        </div>
                                        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                                            <span className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">Suma:</span>
                                            {isAdmin && typeof onUpdateAmount === 'function' ? (
                                                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        min="0"
                                                        value={event.amount || ''}
                                                        onChange={(e) => {
                                                            const amount = parseFloat(e.target.value) || 0;
                                                            onUpdateAmount(event.date, event.eventName, amount);
                                                        }}
                                                        placeholder={event.suggestedAmount > 0 ? event.suggestedAmount.toFixed(2) : "0.00"}
                                                        className="w-full sm:w-32 px-2 py-1 rounded border text-right text-sm
                                                                   bg-white dark:bg-gray-700
                                                                   border-gray-300 dark:border-gray-600
                                                                   text-gray-900 dark:text-gray-100
                                                                   focus:outline-none focus:border-blue-500"
                                                    />
                                                    {event.suggestedAmount > 0 && (event.amount || 0) !== event.suggestedAmount && (
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                onUpdateAmount(event.date, event.eventName, event.suggestedAmount);
                                                            }}
                                                            className="text-xs px-2 py-1 rounded bg-blue-600 hover:bg-blue-700 text-white whitespace-nowrap"
                                                            title={`Użyj sugerowanej kwoty: ${event.suggestedAmount.toFixed(2)} zł`}
                                                        >
                                                            Sugeruj: {event.suggestedAmount.toFixed(2)} zł
                                                        </button>
                                                    )}
                                                </div>
                                            ) : (
                                                <span className="font-semibold text-sm sm:text-base">
                                                    {event.amount > 0 ? `${event.amount.toFixed(2)} zł` : '-'}
                                                    {event.suggestedAmount > 0 && event.amount !== event.suggestedAmount && (
                                                        <span className="text-xs text-gray-400 ml-2 block sm:inline">
                                                            (sugerowane: {event.suggestedAmount.toFixed(2)} zł)
                                                        </span>
                                                    )}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Lista pracowników */}
                                <div className="mt-4 space-y-3">
                                    {event.staff.length === 0 ? (
                                        <div className="text-sm text-gray-500 dark:text-gray-400">
                                            Brak przypisanych pracowników
                                        </div>
                                    ) : (
                                        event.staff.map((staffMember, staffIdx) => {
                                            const employee = employees.find(
                                                (e) => (typeof e === 'string' ? e : e.name) === staffMember.name
                                            );
                                            
                                            return (
                                                <div
                                                    key={staffIdx}
                                                    className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 p-3 rounded
                                                               bg-gray-50 dark:bg-gray-900
                                                               border border-gray-200 dark:border-gray-700"
                                                >
                                                    <div className="flex items-center gap-3 flex-1 min-w-0">
                                                        <Avatar employee={employee || staffMember.name} size={32} />
                                                        <div className="flex-1 min-w-0">
                                                            <div className="font-medium text-sm sm:text-base break-words">{staffMember.name}</div>
                                                            <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                                                                Wynagrodzenie: <span className="font-semibold text-gray-900 dark:text-gray-100">
                                                                    {staffMember.total.toFixed(2)} zł
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {isAdmin && (
                                                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 w-full sm:w-auto">
                                                            {/* Wybór funkcji */}
                                                            <select
                                                                value={staffMember.position || ''}
                                                                onChange={(e) => {
                                                                    const newPosition = e.target.value || null;
                                                                    if (typeof onUpdateRole === 'function') {
                                                                        onUpdateRole(event.date, event.eventName, staffMember.name, newPosition);
                                                                        
                                                                        // Automatycznie ustaw kwotę z paymentRates jeśli nie ma jeszcze zapisanej
                                                                        if (newPosition && typeof onUpdateStaffPayment === 'function') {
                                                                            const empRates = paymentRates[staffMember.name] || {};
                                                                            const defaultRate = empRates[newPosition] || 0;
                                                                            const currentPayment = eventStaffPayments[event.date]?.[event.eventName]?.[staffMember.name] || {};
                                                                            
                                                                            // Ustaw tylko jeśli nie ma jeszcze zapisanej kwoty
                                                                            if (currentPayment.baseAmount === undefined && defaultRate > 0) {
                                                                                handleUpdateBaseAmountLocal(event.date, event.eventName, staffMember.name, defaultRate);
                                                                            }
                                                                        }
                                                                    }
                                                                }}
                                                                className="w-full sm:w-auto px-2 py-1 rounded border text-sm
                                                                           bg-white dark:bg-gray-700
                                                                           border-gray-300 dark:border-gray-600
                                                                           text-gray-900 dark:text-gray-100"
                                                            >
                                                                <option value="">Wybierz funkcję</option>
                                                                {POSITIONS.map((pos) => (
                                                                    <option key={pos.key} value={pos.key}>
                                                                        {pos.label}
                                                                    </option>
                                                                ))}
                                                            </select>

                                                            {/* Kwota za funkcję */}
                                                            <input
                                                                type="number"
                                                                step="0.01"
                                                                min="0"
                                                                value={staffMember.baseAmount || ''}
                                                                onChange={(e) => handleUpdateBaseAmountLocal(event.date, event.eventName, staffMember.name, e.target.value)}
                                                                placeholder="Kwota"
                                                                className="w-full sm:w-24 px-2 py-1 rounded border text-sm text-right
                                                                           bg-white dark:bg-gray-700
                                                                           border-gray-300 dark:border-gray-600
                                                                           text-gray-900 dark:text-gray-100"
                                                            />

                                                            {/* Koszty dodatkowe */}
                                                            <input
                                                                type="number"
                                                                step="0.01"
                                                                min="0"
                                                                value={staffMember.additionalCosts || ''}
                                                                onChange={(e) => handleUpdateAdditionalCostsLocal(event.date, event.eventName, staffMember.name, e.target.value)}
                                                                placeholder="Koszty dodatkowe"
                                                                className="w-full sm:w-32 px-2 py-1 rounded border text-sm text-right
                                                                           bg-white dark:bg-gray-700
                                                                           border-gray-300 dark:border-gray-600
                                                                           text-gray-900 dark:text-gray-100"
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Podsumowanie miesiąca */}
            {monthEvents.length > 0 && (
                <div className="border rounded-lg p-3 sm:p-4
                                bg-white dark:bg-gray-800
                                border-gray-200 dark:border-gray-700">
                    <h2 className="text-base sm:text-lg font-semibold mb-4">📊 Podsumowanie miesiąca</h2>
                    
                    <div className="space-y-3 mb-4">
                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1">
                            <span className="text-sm sm:text-base">Suma za wszystkie wydarzenia:</span>
                            <span className="font-medium text-sm sm:text-base">
                                {monthSummary.totalEventAmounts.toFixed(2)} zł
                            </span>
                        </div>
                        {monthSummary.taxRate > 0 && (
                            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1">
                                <span className="text-sm sm:text-base">Podatek ({monthSummary.taxRate.toFixed(2)}%):</span>
                                <span className="font-medium text-orange-600 dark:text-orange-400 text-sm sm:text-base">
                                    {monthSummary.taxAmount.toFixed(2)} zł
                                </span>
                            </div>
                        )}
                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1">
                            <span className="text-sm sm:text-base">Wynagrodzenia pracowników:</span>
                            <span className="font-medium text-green-600 dark:text-green-400 text-sm sm:text-base">
                                {monthSummary.totalStaffPayments.toFixed(2)} zł
                            </span>
                        </div>
                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1 pt-2 border-t border-gray-200 dark:border-gray-700">
                            <span className="font-semibold text-sm sm:text-base">
                                Różnica (suma wydarzeń - podatek - wynagrodzenia):
                            </span>
                            <span className={`text-base sm:text-lg font-bold ${
                                monthSummary.difference >= 0 
                                    ? 'text-green-600 dark:text-green-400' 
                                    : 'text-red-600 dark:text-red-400'
                            }`}>
                                {monthSummary.difference.toFixed(2)} zł
                            </span>
                        </div>
                    </div>

                    {/* Podsumowanie dla każdej osoby */}
                    {Object.keys(employeeMonthlyTotals).length > 0 && (
                        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                            <h3 className="font-semibold mb-3 text-sm sm:text-base">Podsumowanie dla każdej osoby:</h3>
                            <div className="space-y-2">
                                {Object.entries(employeeMonthlyTotals)
                                    .sort((a, b) => b[1] - a[1])
                                    .map(([empName, total]) => {
                                        const employee = employees.find(
                                            (e) => (typeof e === 'string' ? e : e.name) === empName
                                        );
                                        
                                        return (
                                            <div key={empName} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-2">
                                                <div className="flex items-center gap-2 min-w-0">
                                                    <Avatar employee={employee || empName} size={20} />
                                                    <span className="text-sm sm:text-base truncate">{empName}</span>
                                                </div>
                                                <span className="font-medium text-sm sm:text-base">
                                                    {total.toFixed(2)} zł
                                                </span>
                                            </div>
                                        );
                                    })}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
